import {Fragment, ReactNode, useEffect, useLayoutEffect, useState} from 'react';
import {Header} from '@components/Header';
import {useDispatch} from '@utils/hooks/common';
import {Storage} from '@utils/storage';
import {globalBoardSettingsSlice, aiSettingsSlice, featureFlagsSlice} from '@reducers';
import {Container, Alert} from '@goproblems/ui';
import {useFetchSiteSettingsQuery} from '@api';
import ToastNotification from '../ToastNotification';
import {ToastContainer, ToastContainerProps} from 'react-toastify';
import {useTheme} from '@components/theme/useTheme';

const ToastContainerThemeAware = (props: ToastContainerProps) => {
    const theme = useTheme();
    return <ToastContainer theme={theme} {...props} />;
};

export const Layout = ({
    containerClassName = '!pt-4',
    children,
}: {
    containerClassName?: string;
    children?: ReactNode;
}) => {
    const dispatch = useDispatch();
    const {data: siteSettings} = useFetchSiteSettingsQuery();
    const siteBanners = siteSettings?.siteBanners;
    const featureFlags = siteSettings?.featureFlags;
    const [dismissedBannerIds, setDismissedBannerIds] = useState<number[]>(
        []
    );

    useLayoutEffect(() => {
        // persisted state
        dispatch(
            globalBoardSettingsSlice.actions.setGlobalBoardSettings(
                Storage.getObjData('globalBoardSettings')
            )
        );
        dispatch(
            aiSettingsSlice.actions.setAiSettings(
                Storage.getObjData('aiSettings')
            )
        );
        if (Storage.getObjData('featureFlags')) {
            dispatch(
                featureFlagsSlice.actions.setFeatureFlags(
                    Storage.getObjData('featureFlags')
                )
            );
        }
    }, [dispatch]);

    useEffect(() => {
        if (featureFlags) {
            dispatch(featureFlagsSlice.actions.setFeatureFlags(featureFlags));
        }
    }, [featureFlags]);

    return (
        <Fragment>
            <ToastContainerThemeAware closeOnClick />
            <ToastNotification />
            <Header />
            <div className="pointer-events-none fixed inset-x-0 top-0 z-[1030]">
                <Container fluid>
                    {siteBanners?.map(banner => {
                        if (
                            dismissedBannerIds.includes(banner.id) ||
                            Storage.getObjData<boolean>(
                                `banner_${banner.id}_dismissed`
                            ) === true
                        ) {
                            return null;
                        }
                        return (
                            <Alert
                                key={banner.id}
                                className="pointer-events-auto my-2 shadow-lg"
                                variant={banner.variant}
                                dismissible
                                onClose={() => {
                                    Storage.storeObjData(
                                        `banner_${banner.id}_dismissed`,
                                        true
                                    );
                                    setDismissedBannerIds(previousIds => [
                                        ...previousIds,
                                        banner.id,
                                    ]);
                                }}>
                                {banner.heading && (
                                    <Alert.Heading>{banner.heading}</Alert.Heading>
                                )}
                                {banner.content && (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: banner.content,
                                        }}></div>
                                )}
                            </Alert>
                        );
                    })}
                </Container>
            </div>
            <Container as="main" fluid className={containerClassName}>
                {children}
            </Container>
        </Fragment>
    );
};
